/**
 * Relatórios › Mensagens Agendadas — controle do que foi programado.
 *
 * Admin vê o de toda a equipe; o atendente, só o que ele agendou (a API já
 * filtra). Pendente e falha podem ser editadas/reagendadas ou canceladas;
 * enviada e cancelada ficam só de registro. A aba Pendentes se atualiza
 * sozinha a cada 30 s (o despachante envia no mesmo ritmo).
 */
import { Button } from '@/shared/components/ui/Button';
import { ApiError } from '@/shared/lib/api-client';
import { cn } from '@/shared/lib/cn';
import { confirm } from '@/shared/lib/confirm';
import { useAuthStore } from '@/shared/stores/auth-store';
import {
  CalendarClock,
  FileText,
  Image as ImageIcon,
  Loader2,
  MessageSquarePlus,
  Music,
  Pencil,
  RefreshCw,
  Video,
  XCircle,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  type FiltroDeStatus,
  type ScheduledMessage,
  useCancelScheduledMessage,
  useScheduledMessages,
} from './api';
import {
  type ContextoDoAgendamento,
  ScheduleMessageModal,
} from './components/ScheduleMessageModal';
import { formatarQuando } from './lib';

const ABAS: { status: FiltroDeStatus; label: string }[] = [
  { status: 'pending', label: 'Pendentes' },
  { status: 'sent', label: 'Enviadas' },
  { status: 'failed', label: 'Falharam' },
  { status: 'cancelled', label: 'Canceladas' },
  { status: 'all', label: 'Todas' },
];

const ICONE_DO_TIPO = { image: ImageIcon, audio: Music, video: Video, document: FileText };

const VAZIO: Record<FiltroDeStatus, string> = {
  pending: 'Nenhuma mensagem agendada para enviar.',
  sent: 'Nenhuma mensagem agendada foi enviada ainda.',
  failed: 'Nenhuma mensagem agendada falhou.',
  cancelled: 'Nenhum agendamento cancelado.',
  all: 'Nenhuma mensagem agendada.',
};

export function MensagensAgendadasPage() {
  const isAdmin = useAuthStore((s) => s.user?.profile) === 'admin';
  const [status, setStatus] = useState<FiltroDeStatus>('pending');
  const [editando, setEditando] = useState<ContextoDoAgendamento | null>(null);
  const listaQ = useScheduledMessages(status);
  const cancelarMut = useCancelScheduledMessage();
  const sentinelaRef = useRef<HTMLTableRowElement>(null);

  const itens = listaQ.data?.pages.flatMap((p) => p.items) ?? [];
  const contagem = listaQ.data?.pages[0]?.counts;
  const agora = new Date();
  const colunas = isAdmin ? 7 : 6;

  // Rolagem infinita (mesmo padrão de Contatos).
  useEffect(() => {
    const el = sentinelaRef.current;
    if (!el || !listaQ.hasNextPage || listaQ.isFetchingNextPage) return;
    const obs = new IntersectionObserver(
      (entradas) => {
        if (entradas.some((e) => e.isIntersecting)) void listaQ.fetchNextPage();
      },
      { rootMargin: '200px' },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [listaQ.hasNextPage, listaQ.isFetchingNextPage, listaQ.fetchNextPage]);

  const cancelar = async (m: ScheduledMessage): Promise<void> => {
    const ok = await confirm({
      title: 'Cancelar este agendamento?',
      description: `A mensagem para ${m.contact.name ?? m.contact.phone ?? 'o contato'} não será enviada.`,
      confirmText: 'Cancelar agendamento',
      cancelText: 'Voltar',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await cancelarMut.mutateAsync(m.id);
      toast.success('Agendamento cancelado');
    } catch (err) {
      if (err instanceof ApiError) {
        toast.error(err.problem.title, { description: err.problem.detail });
      } else {
        toast.error('Não foi possível cancelar.');
      }
    }
  };

  return (
    <div className="flex h-full min-w-0 flex-1 flex-col overflow-hidden bg-n-50">
      <header className="flex flex-shrink-0 items-center justify-between border-n-200 border-b bg-n-0 px-5 py-4">
        <div>
          <h1 className="font-bold text-[18px] text-n-900">Mensagens Agendadas</h1>
          <p className="text-[12px] text-n-500">
            {isAdmin ? 'Mensagens programadas por toda a equipe.' : 'Mensagens que você programou.'}{' '}
            Para agendar, use o ícone de calendário no campo de mensagem da conversa (canais QR
            Code).
          </p>
        </div>
        <Button variant="outline" onClick={() => void listaQ.refetch()}>
          <RefreshCw className={cn(listaQ.isFetching && 'animate-spin')} />
          Atualizar
        </Button>
      </header>

      <div className="flex flex-shrink-0 items-center gap-1.5 border-n-200 border-b bg-n-0 px-5 py-2.5">
        {ABAS.map((aba) => {
          const ativa = aba.status === status;
          const n =
            aba.status === 'pending'
              ? contagem?.pending
              : aba.status === 'failed'
                ? contagem?.failed
                : undefined;
          return (
            <button
              key={aba.status}
              type="button"
              onClick={() => setStatus(aba.status)}
              className={cn(
                'flex h-[30px] items-center gap-1.5 rounded-md px-3 font-semibold text-[12px] transition-colors',
                ativa ? 'bg-brand-50 text-brand-700' : 'text-n-600 hover:bg-n-100',
              )}
            >
              {aba.label}
              {n !== undefined && n > 0 && (
                <span
                  className={cn(
                    'min-w-[18px] rounded-pill px-1.5 text-center text-[10px] leading-[16px]',
                    aba.status === 'failed'
                      ? 'bg-danger-500 text-white'
                      : 'bg-brand-500 text-white',
                  )}
                >
                  {n}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-5">
        <div className="flex min-h-0 flex-col overflow-hidden rounded-md border border-n-200 bg-n-0 shadow-sm">
          <div className="min-h-0 flex-1 overflow-auto">
            <table className="w-full min-w-[860px] text-[12px]">
              <thead className="sticky top-0 z-10 border-n-200 border-b bg-n-50 text-left">
                <tr>
                  {[
                    'Cliente',
                    'Mensagem',
                    'Canal',
                    ...(isAdmin ? ['Agendado por'] : []),
                    'Envio',
                    'Status',
                  ].map((t) => (
                    <th
                      key={t}
                      className="whitespace-nowrap px-[14px] py-[10px] font-bold text-[11px] text-n-500 uppercase tracking-[.05em]"
                    >
                      {t}
                    </th>
                  ))}
                  <th className="whitespace-nowrap px-[14px] py-[10px] text-right font-bold text-[11px] text-n-500 uppercase tracking-[.05em]">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody>
                {listaQ.isLoading && (
                  <tr>
                    <td colSpan={colunas} className="py-12 text-center text-n-500">
                      <Loader2 size={20} className="mx-auto animate-spin text-n-400" />
                    </td>
                  </tr>
                )}
                {!listaQ.isLoading && itens.length === 0 && (
                  <tr>
                    <td colSpan={colunas} className="py-12 text-center text-n-500">
                      <CalendarClock size={32} className="mx-auto mb-2 text-n-300" />
                      {VAZIO[status]}
                    </td>
                  </tr>
                )}
                {itens.map((m) => (
                  <Linha
                    key={m.id}
                    m={m}
                    agora={agora}
                    mostrarAutor={isAdmin}
                    onEditar={() => setEditando({ tipo: 'editar', agendamento: m })}
                    onCancelar={() => void cancelar(m)}
                  />
                ))}
                {listaQ.hasNextPage && (
                  <tr ref={sentinelaRef}>
                    <td colSpan={colunas} className="py-4 text-center text-[12px] text-n-500">
                      {listaQ.isFetchingNextPage && (
                        <span className="inline-flex items-center gap-2">
                          <Loader2 size={14} className="animate-spin" /> Carregando mais…
                        </span>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <ScheduleMessageModal contexto={editando} onClose={() => setEditando(null)} />
    </div>
  );
}

function Linha({
  m,
  agora,
  mostrarAutor,
  onEditar,
  onCancelar,
}: {
  m: ScheduledMessage;
  agora: Date;
  mostrarAutor: boolean;
  onEditar: () => void;
  onCancelar: () => void;
}) {
  const IconeArquivo = m.message_type !== 'text' ? ICONE_DO_TIPO[m.message_type] : null;
  const mexivel = m.status === 'pending' || m.status === 'failed';
  return (
    <tr className="border-n-100 border-b align-top last:border-b-0 hover:bg-n-50">
      <td className="px-[14px] py-[11px]">
        <div className="font-semibold text-[13px] text-n-900">
          {m.contact.name ?? m.contact.phone ?? 'Sem nome'}
        </div>
        {m.contact.phone && m.contact.name && (
          <div className="text-[11px] text-n-500">{m.contact.phone}</div>
        )}
      </td>
      <td className="max-w-[320px] px-[14px] py-[11px]">
        {IconeArquivo && (
          <div className="mb-0.5 flex items-center gap-1 text-[11px] text-n-500">
            <IconeArquivo size={12} strokeWidth={1.75} className="flex-shrink-0" />
            <span className="truncate">{m.media?.name ?? 'Arquivo'}</span>
          </div>
        )}
        {m.body && (
          <div className="line-clamp-2 whitespace-pre-line text-[12px] text-n-700">{m.body}</div>
        )}
      </td>
      <td className="whitespace-nowrap px-[14px] py-[11px] text-n-700">
        {m.channel.name ?? m.channel.number ?? '—'}
      </td>
      {mostrarAutor && (
        <td className="whitespace-nowrap px-[14px] py-[11px] text-n-700">{m.created_by.name}</td>
      )}
      <td className="whitespace-nowrap px-[14px] py-[11px]">
        <div className="text-n-800">{formatarQuando(m.send_at, agora)}</div>
        {m.open_ticket && (
          <div
            className="mt-0.5 flex items-center gap-1 text-[11px] text-n-500"
            title="Se o cliente não tiver conversa aberta na hora, abre um novo atendimento"
          >
            <MessageSquarePlus size={11} strokeWidth={2} /> Abre atendimento
          </div>
        )}
      </td>
      <td className="max-w-[240px] px-[14px] py-[11px]">
        <StatusBadge m={m} agora={agora} />
      </td>
      <td className="whitespace-nowrap px-[14px] py-[11px] text-right">
        {mexivel && (
          <div className="inline-flex gap-1">
            <button
              type="button"
              onClick={onEditar}
              title={m.status === 'failed' ? 'Reagendar' : 'Editar'}
              aria-label={m.status === 'failed' ? 'Reagendar' : 'Editar'}
              className="flex h-[30px] w-[30px] items-center justify-center rounded-md text-n-500 hover:bg-n-100 hover:text-brand-600"
            >
              <Pencil size={15} strokeWidth={1.75} />
            </button>
            <button
              type="button"
              onClick={onCancelar}
              title="Cancelar agendamento"
              aria-label="Cancelar agendamento"
              className="flex h-[30px] w-[30px] items-center justify-center rounded-md text-n-500 hover:bg-danger-50 hover:text-danger-600"
            >
              <XCircle size={15} strokeWidth={1.75} />
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}

function StatusBadge({ m, agora }: { m: ScheduledMessage; agora: Date }) {
  const base =
    'inline-flex items-center gap-1 rounded-sm px-[6px] py-[2px] font-semibold text-[10px] leading-[15px]';
  switch (m.status) {
    case 'pending':
      return <span className={cn(base, 'bg-brand-100 text-brand-700')}>Pendente</span>;
    case 'sending':
      return (
        <span className={cn(base, 'bg-brand-100 text-brand-700')}>
          <Loader2 size={10} className="animate-spin" /> Enviando
        </span>
      );
    case 'sent':
      return (
        <div>
          <span className={cn(base, 'bg-success-50 text-success-700')}>Enviada</span>
          {m.processed_at && (
            <div className="mt-0.5 text-[11px] text-n-500">
              {formatarQuando(m.processed_at, agora)}
            </div>
          )}
        </div>
      );
    case 'failed':
      return (
        <div>
          <span className={cn(base, 'bg-danger-50 text-danger-600')}>Falhou</span>
          {m.error_message && (
            <div className="mt-0.5 text-[11px] text-danger-600">{m.error_message}</div>
          )}
        </div>
      );
    case 'cancelled':
      return <span className={cn(base, 'bg-n-200 text-n-700')}>Cancelada</span>;
  }
}
